/**
 * Scoped Permissions Hook
 * 
 * Provides permission checks scoped to a specific context (process, session, item, etc.)
 * Used by components to determine what actions the user can perform at this scope.
 * 
 * Usage:
 * ```tsx
 * const { can, canAny, canAll, permissions } = useScopedPermissions({
 *   kind: 'process',
 *   id: processId
 * });
 * 
 * if (can('process.prioritize')) {
 *   // Show prioritization UI
 * }
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUserStore } from '../../stores/prioritization/userStore';
import type {
  PermissionAction,
  PermissionScope,
  EffectivePermission,
  ScopeKind,
} from '../../types/prioritization/permissions';
import { createScopeChain } from '../../types/prioritization/permissions';

// ============================================================================
// Types
// ============================================================================

export interface UseScopedPermissionsOptions {
  /** Automatically load permissions on mount */
  autoLoad?: boolean;
  /** Custom scope chain (if not using the shorthand) */
  scopeChain?: PermissionScope;
}

export interface UseScopedPermissionsResult {
  /** Check if user has a specific permission at this scope */
  can: (action: PermissionAction) => boolean;
  /** Check if user has ANY of the specified permissions */
  canAny: (...actions: PermissionAction[]) => boolean;
  /** Check if user has ALL of the specified permissions */
  canAll: (...actions: PermissionAction[]) => boolean;
  /** All effective permissions at this scope */
  permissions: Map<PermissionAction, EffectivePermission>;
  /** Whether permissions are being loaded */
  loading: boolean;
  /** Any error that occurred during loading */
  error: string | null;
  /** Manually refresh permissions */
  refresh: () => Promise<void>;
  /** The scope being checked */
  scope: PermissionScope;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook for checking permissions at a specific scope
 */
export function useScopedPermissions(
  scope: PermissionScope,
  options: UseScopedPermissionsOptions = {}
): UseScopedPermissionsResult {
  const { autoLoad = true } = options;
  
  const userStore = useUserStore();
  const { userId, permissionsLoaded, loadPermissions, can: storeCan } = userStore;
  
  const [permissions, setPermissions] = useState<Map<PermissionAction, EffectivePermission>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load permissions on mount if autoLoad is enabled
  useEffect(() => {
    if (autoLoad && !permissionsLoaded && userId) {
      loadPermissions();
    }
  }, [autoLoad, permissionsLoaded, userId, loadPermissions]);

  // Load effective permissions for this scope
  const loadScopedPermissions = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const perms = await userStore.getPermissionsAtScope(scope);
      setPermissions(perms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [userId, scope, userStore]);

  // Refresh permissions
  const refresh = useCallback(async () => {
    await loadPermissions();
    await loadScopedPermissions();
  }, [loadPermissions, loadScopedPermissions]);

  // Check single permission
  const can = useCallback((action: PermissionAction): boolean => {
    // Use the store's synchronous check which uses cached data
    return storeCan(action, scope);
  }, [storeCan, scope]);

  // Check if user has ANY of the permissions
  const canAny = useCallback((...actions: PermissionAction[]): boolean => {
    return actions.some(action => can(action));
  }, [can]);

  // Check if user has ALL of the permissions
  const canAll = useCallback((...actions: PermissionAction[]): boolean => {
    return actions.every(action => can(action));
  }, [can]);

  return {
    can,
    canAny,
    canAll,
    permissions,
    loading,
    error,
    refresh,
    scope,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for process-level permissions
 */
export function useProcessPermissions(
  processId: string,
  groupId?: string
): UseScopedPermissionsResult {
  const scope = useMemo(
    () => createScopeChain(undefined, undefined, processId, groupId),
    [processId, groupId]
  );
  return useScopedPermissions(scope);
}

/**
 * Hook for session-level permissions
 */
export function useSessionPermissions(
  sessionId: string,
  processId?: string,
  groupId?: string
): UseScopedPermissionsResult {
  const scope = useMemo(
    () => createScopeChain(undefined, sessionId, processId, groupId),
    [sessionId, processId, groupId]
  );
  return useScopedPermissions(scope);
}

/**
 * Hook for item-level permissions
 */
export function useItemPermissions(
  itemId: string,
  processId?: string,
  groupId?: string
): UseScopedPermissionsResult {
  const scope = useMemo(
    () => createScopeChain(itemId, undefined, processId, groupId),
    [itemId, processId, groupId]
  );
  return useScopedPermissions(scope);
}

/**
 * Hook for group-level permissions
 */
export function useGroupPermissions(groupId: string): UseScopedPermissionsResult {
  const scope = useMemo(
    () => createScopeChain(undefined, undefined, undefined, groupId),
    [groupId]
  );
  return useScopedPermissions(scope);
}

/**
 * Hook for organization-level (global) permissions
 */
export function useOrgPermissions(): UseScopedPermissionsResult {
  const scope = useMemo(
    () => ({ kind: 'organization' as ScopeKind, id: '*' }),
    []
  );
  return useScopedPermissions(scope);
}

// ============================================================================
// Permission Check Utilities
// ============================================================================

/**
 * Check if user can edit items at the current scope
 */
export function useCanEditItems(scope: PermissionScope): boolean {
  const { can } = useScopedPermissions(scope);
  return can('items.edit');
}

/**
 * Check if user can manage criteria
 */
export function useCanManageCriteria(scope: PermissionScope): boolean {
  const { canAll } = useScopedPermissions(scope);
  return canAll('criteria.view', 'criteria.edit');
}

/**
 * Check if user can facilitate sessions
 */
export function useCanFacilitate(scope: PermissionScope): boolean {
  const { can } = useScopedPermissions(scope);
  return can('session.facilitate');
}

/**
 * Check if user can prioritize
 */
export function useCanPrioritize(scope: PermissionScope): boolean {
  const { can } = useScopedPermissions(scope);
  return can('process.prioritize');
}

/**
 * Check if user has admin permissions
 */
export function useIsAdmin(): boolean {
  const { canAny } = useOrgPermissions();
  return canAny('admin.users', 'admin.groups', 'admin.roles', 'admin.policies');
}
